import { useState, useMemo } from 'react';
import { ITimelineEvent } from '@/types/timeline';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'debug-time-machine-ui';
import { 
  Component,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Activity,
  Clock,
  Zap,
  Eye,
  Bug,
  TrendingUp,
  Database,
  RefreshCw
} from 'lucide-react';

interface ComponentViewerProps {
  events: ITimelineEvent[];
}

interface ComponentInfo {
  name: string;
  totalEvents: number;
  errorCount: number;
  warningCount: number;
  renderCount: number;
  lastActivity: number;
  averageRenderTime: number;
  stateChanges: number;
  apiCalls: number;
  userInteractions: number;
  events: ITimelineEvent[];
}

interface ComponentTreeNode {
  name: string;
  info: ComponentInfo;
  children: ComponentTreeNode[];
  level: number;
}

// 헬퍼 함수들을 컴포넌트 외부로 이동
const extractComponentFromTitle = (title: string): string | null => {
  const patterns = [
    /State change in (\w+)/i,
    /(\w+Component|\w+View|\w+Page)\b/i,
    /Performance: (\w+)/i,
    /(\w+):\s*User Action/i,
  ];
  
  for (const pattern of patterns) {
    const match = title.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
};

const isChildComponent = (parentName: string, childName: string): boolean => {
  // 간단한 부모-자식 관계 추정 로직
  const parentLower = parentName.toLowerCase();
  const childLower = childName.toLowerCase();
  
  // 부모 이름이 자식 이름에 포함되거나, 일반적인 부모-자식 패턴
  return (
    childLower.includes(parentLower.replace('component', '')) ||
    (parentLower === 'app' && !childLower.includes('app')) ||
    (parentLower.includes('page') && childLower.includes('component')) ||
    (parentLower.includes('dashboard') && !childLower.includes('dashboard'))
  );
};

const ComponentViewer = ({ events }: ComponentViewerProps) => {
  const [expandedComponents, setExpandedComponents] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'name' | 'activity' | 'errors' | 'renders'>('activity');
  const [showOnlyActive, setShowOnlyActive] = useState(false);

  // 컴포넌트별 정보 집계
  const componentInfoMap = useMemo(() => {
    const infoMap = new Map<string, ComponentInfo>();
    
    events.forEach(event => {
      const componentName = event.data?.componentName || extractComponentFromTitle(event.title) || 'Unknown';
      
      if (!infoMap.has(componentName)) {
        infoMap.set(componentName, {
          name: componentName,
          totalEvents: 0,
          errorCount: 0,
          warningCount: 0,
          renderCount: 0,
          lastActivity: 0,
          averageRenderTime: 0,
          stateChanges: 0,
          apiCalls: 0,
          userInteractions: 0,
          events: [],
        });
      }

      const info = infoMap.get(componentName)!;
      info.totalEvents++;
      info.lastActivity = Math.max(info.lastActivity, event.timestamp);
      info.events.push(event);

      // 이벤트 타입별 카운트
      if (event.isError || event.severity === 'critical') {
        info.errorCount++;
      } else if (event.severity === 'warning') {
        info.warningCount++;
      }

      switch (event.type) {
        case 'state_change':
          info.stateChanges++;
          info.renderCount++;
          if (event.data?.renderTime) {
            info.averageRenderTime = (info.averageRenderTime * (info.renderCount - 1) + event.data.renderTime) / info.renderCount;
          }
          break;
        case 'performance':
          info.renderCount++;
          if (event.data?.renderTime) {
            info.averageRenderTime = (info.averageRenderTime * (info.renderCount - 1) + event.data.renderTime) / info.renderCount;
          }
          break;
        case 'api_call':
          info.apiCalls++;
          break;
        case 'user_action':
          info.userInteractions++;
          break;
      }
    });

    return infoMap;
  }, [events]);

  // 컴포넌트 트리 구성
  const componentTree = useMemo(() => {
    const components = Array.from(componentInfoMap.values());
    
    // 필터링
    const filteredComponents = showOnlyActive 
      ? components.filter(comp => Date.now() - comp.lastActivity < 300000) // 5분 이내
      : components;

    // 정렬
    const sortedComponents = filteredComponents.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'activity':
          return b.lastActivity - a.lastActivity;
        case 'errors':
          return b.errorCount - a.errorCount;
        case 'renders':
          return b.renderCount - a.renderCount;
        default:
          return b.lastActivity - a.lastActivity;
      }
    });

    // 간단한 트리 구조 생성 (부모-자식 관계 추정)
    const tree: ComponentTreeNode[] = [];
    const processed = new Set<string>();

    sortedComponents.forEach(component => {
      if (processed.has(component.name)) return;

      const node: ComponentTreeNode = {
        name: component.name,
        info: component,
        children: [],
        level: 0,
      };

      // 자식 컴포넌트 찾기 (이름 패턴 기반)
      const children = sortedComponents.filter(child => 
        child.name !== component.name &&
        !processed.has(child.name) &&
        isChildComponent(component.name, child.name)
      );

      children.forEach(child => {
        node.children.push({
          name: child.name,
          info: child,
          children: [],
          level: 1,
        });
        processed.add(child.name);
      });

      tree.push(node);
      processed.add(component.name);
    });

    return tree;
  }, [componentInfoMap, sortBy, showOnlyActive]);

  const toggleComponent = (componentName: string) => {
    setExpandedComponents(prev => {
      const next = new Set(prev);
      if (next.has(componentName)) {
        next.delete(componentName);
      } else {
        next.add(componentName);
      }
      return next;
    });
  };

  const getHealthColor = (info: ComponentInfo) => {
    if (info.errorCount > 0) return 'text-red-600';
    if (info.warningCount > 0) return 'text-amber-600';
    if (info.averageRenderTime > 50) return 'text-orange-600';
    return 'text-emerald-600';
  };

  const getActivityLevel = (lastActivity: number) => {
    const minutesAgo = (Date.now() - lastActivity) / 60000;
    if (minutesAgo < 1) return { level: 'Very Active', color: 'bg-emerald-500' };
    if (minutesAgo < 5) return { level: 'Active', color: 'bg-blue-500' };
    if (minutesAgo < 30) return { level: 'Moderate', color: 'bg-amber-500' };
    return { level: 'Inactive', color: 'bg-slate-400' };
  };

  const ComponentNode = ({ node, isChild = false }: { node: ComponentTreeNode; isChild?: boolean }) => {
    const isExpanded = expandedComponents.has(node.name);
    const hasEvents = node.info.events.length > 0;
    const activity = getActivityLevel(node.info.lastActivity);

    return (
      <div className={`${isChild ? 'ml-6 border-l-2 border-slate-200 dark:border-slate-700 pl-4' : ''}`}>
        <Card className="mb-3">
          <Collapsible open={isExpanded} onOpenChange={() => toggleComponent(node.name)}>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      {hasEvents ? (
                        isExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )
                      ) : (
                        <Component className="w-4 h-4 text-slate-400" />
                      )}
                      <Component className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{node.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <div className={`w-2 h-2 rounded-full ${activity.color}`} />
                        <span className="text-xs text-muted-foreground">{activity.level}</span>
                        <span className="text-xs text-muted-foreground">•</span>
                        <span className="text-xs text-muted-foreground">
                          {node.info.totalEvents} events
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {node.info.errorCount > 0 && (
                      <Badge variant="destructive" className="text-xs">
                        {node.info.errorCount} errors
                      </Badge>
                    )}
                    {node.info.warningCount > 0 && (
                      <Badge variant="outline" className="text-xs">
                        {node.info.warningCount} warnings
                      </Badge>
                    )}
                    <Badge variant="secondary" className="text-xs">
                      {node.info.renderCount} renders
                    </Badge>
                  </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>

            <CollapsibleContent>
              <CardContent className="pt-0">
                {/* 컴포넌트 통계 */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div className="text-center p-3 border rounded-lg">
                    <Activity className="w-5 h-5 mx-auto mb-1 text-blue-500" />
                    <div className="text-lg font-bold">{node.info.stateChanges}</div>
                    <div className="text-xs text-muted-foreground">State Changes</div>
                  </div>
                  
                  <div className="text-center p-3 border rounded-lg">
                    <Eye className="w-5 h-5 mx-auto mb-1 text-purple-500" />
                    <div className="text-lg font-bold">{node.info.userInteractions}</div>
                    <div className="text-xs text-muted-foreground">User Actions</div>
                  </div>
                  
                  <div className="text-center p-3 border rounded-lg">
                    <Database className="w-5 h-5 mx-auto mb-1 text-emerald-500" />
                    <div className="text-lg font-bold">{node.info.apiCalls}</div>
                    <div className="text-xs text-muted-foreground">API Calls</div>
                  </div>
                  
                  <div className="text-center p-3 border rounded-lg">
                    <Zap className="w-5 h-5 mx-auto mb-1 text-orange-500" />
                    <div className={`text-lg font-bold ${getHealthColor(node.info)}`}>
                      {node.info.averageRenderTime > 0 ? `${Math.round(node.info.averageRenderTime)}ms` : 'N/A'}
                    </div>
                    <div className="text-xs text-muted-foreground">Avg Render</div>
                  </div>
                </div>

                {/* 최근 이벤트 */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Recent Events
                  </h4>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {node.info.events.slice(-5).reverse().map((event, index) => (
                      <div key={`${event.id}-${event.timestamp}-${index}`} className="flex items-center justify-between p-2 border rounded text-sm">
                        <div className="flex items-center gap-2">
                          {event.isError ? (
                            <Bug className="w-3 h-3 text-red-500" />
                          ) : event.severity === 'warning' ? (
                            <AlertTriangle className="w-3 h-3 text-amber-500" />
                          ) : (
                            <Activity className="w-3 h-3 text-blue-500" />
                          )}
                          <span className="truncate max-w-xs">{event.title}</span>
                        </div>
                        <span className="text-xs text-muted-foreground font-mono">
                          {new Date(event.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    ))}
                    {node.info.events.length === 0 && (
                      <div className="text-center py-4 text-muted-foreground text-sm">
                        No events recorded
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {/* 자식 컴포넌트 렌더링 */}
        {node.children.length > 0 && (
          <div className="ml-4">
            {node.children.map((child, index) => (
              <ComponentNode key={`${child.name}-child-${index}`} node={child} isChild={true} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* 컨트롤 헤더 */}
      <div className="flex items-center justify-between mb-4 p-4 border-b">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Component className="w-5 h-5" />
            Component Tree ({componentInfoMap.size} components)
          </h3>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant={showOnlyActive ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowOnlyActive(!showOnlyActive)}
          >
            <Activity className="w-4 h-4 mr-2" />
            Active Only
          </Button>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-3 py-1 text-sm border rounded-md"
          >
            <option value="activity">Sort by Activity</option>
            <option value="name">Sort by Name</option>
            <option value="errors">Sort by Errors</option>
            <option value="renders">Sort by Renders</option>
          </select>
        </div>
      </div>

      {/* 컴포넌트 트리 */}
      <div className="flex-1 overflow-y-auto px-4">
        {componentTree.length > 0 ? (
          <div className="space-y-2">
            {componentTree.map((node, index) => (
              <ComponentNode key={`${node.name}-${index}`} node={node} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Component className="w-12 h-12 mx-auto mb-4 text-slate-400" />
            <h3 className="text-lg font-medium text-slate-600 dark:text-slate-300 mb-2">
              No Components Found
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {showOnlyActive 
                ? "No components have been active recently. Try disabling 'Active Only' filter."
                : "Start interacting with your application to see components appear here."
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComponentViewer;
